import { useForm, SubmitHandler, Controller, FieldErrorsImpl, FieldValues } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { FaCity } from 'react-icons/fa'
import { MdOutlineTravelExplore } from 'react-icons/md'
import TitleHeader from '../../../components/title-header/TitleHeader'
import InputField from '../../../components/input-field/InputField'
import SelectField from '../../../components/select-field/SelectField'
import { NewReservePilgrimHeaderData, transportationSelectOptions } from '../constants'
import {
  IntegerNumberRegex,
  PersianMobileNumberRegex,
  PersianTelephoneNumberRegex,
} from '../../../utils/regex'
import { useTranslation } from 'react-i18next'
import { HiOutlineUsers } from 'react-icons/hi2'
import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../../hooks/useStore'
import {
  clearStates,
  setReservePilgrimList,
  setCity,
  setProvince,
  setTransportationType,
  setPhoneNumber,
  setMobileNumber,
  setZone,
  createPreReservePublic,
} from '../../../store/features/per-reserve/publicPreReserve/publicPreReserveSlice'
import HeaderInfo from '../components/HeaderInfo'
import PilgrimCard from '../components/PilgrimCard'
import Loader from '../../../components/loader/Loader'
import Button from '../../../components/buttons/Button'
import CancleButton from '../../../components/buttons/SecendaryButton'
import PlusIcon from '../../../components/icons/PlusIcon'
import { setAuthForm } from '../../../store/features/auth/authSlice'
import { AuthForm } from '../../../store/features/auth/type'
import FormMain from '../../../components/page-container/FormMain'
import FormContainer from '../../../components/page-container/FormContainer'
import FormHeader from '../../../components/page-container/FormHeader'
import { TCreatePreReserve } from '../../../store/features/per-reserve/publicPreReserve/types'
import { ToastType } from '../../../types/generalType'
import checkHasLeader from '../../../utils/checkHasLeader'
import toastify from '../../../utils/toastify'
import useProvince from '../../../hooks/useProvince'
import useCities from '../../../hooks/useCities'

interface IForms {
  transportationType: string
  mobilenumber: string
  phoneNumber: string
  province?: string
  city?: string
  zone?: string
  birthDate?: string
}

const schema: yup.SchemaOf<IForms> = yup.object({
  province: yup.string().required('requiedFeild'),
  city: yup.string().required('requiedFeild'),
  zone: yup.string().matches(IntegerNumberRegex, {
    message: 'plusIntegerNumber',
    excludeEmptyString: true,
  }),
  transportationType: yup.string().required('requiedFeild'),
  mobilenumber: yup
    .string()
    .matches(PersianMobileNumberRegex, {
      message: 'mobilePattern',
      excludeEmptyString: true,
    })
    .matches(IntegerNumberRegex, {
      message: 'integerNumber',
      excludeEmptyString: true,
    })
    .min(11, 'mobileLength')
    .max(11, 'mobileLength')
    .required('requiedFeild'),
  phoneNumber: yup
    .string()
    .matches(PersianTelephoneNumberRegex, {
      message: 'telephonePattern',
      excludeEmptyString: true,
    })
    .matches(IntegerNumberRegex, {
      message: 'integerNumber',
      excludeEmptyString: true,
    })
    .min(11, 'telephoneLength')
    .max(11, 'telephoneLength')
    .required('requiedFeild'),
  birthDate: yup.string(),
})

const PublicPilgrimsPreReserve = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<IForms>({
    resolver: yupResolver(schema),
  })
  const { provinceLoading, provinceSelectOptions } = useProvince()
  const { citiesSelectOptions, cityLoadin } = useCities(getValues('province') as string)

  const { loading: createLoading } = useAppSelector(
    (state) => state.reserveNewPreReserve.createPreReserve,
  )
  const { pilgrims, city, mobilenumber, phoneNumber, province, transportationType, zone } =
    useAppSelector((state) => state.publicPreReserve.preRserve)

  useEffect(() => {
    setValue('city', '')
  }, [watch('province')])

  useEffect(() => {
    if (province) {
      const state = provinceSelectOptions?.find((item) => item.label == province)

      setValue('province', state?.value as string)
    }
  }, [province, provinceSelectOptions])

  useEffect(() => {
    if (city) {
      const selectCity = citiesSelectOptions?.find((item) => item.label == city)

      setValue('city', selectCity?.value as string)
    }
  }, [city, citiesSelectOptions])

  useEffect(() => {
    if (zone) {
      setValue('zone', zone as string)
    }
    if (transportationType) setValue('transportationType', transportationType as string)
    if (phoneNumber) setValue('phoneNumber', phoneNumber as string)
    if (mobilenumber) setValue('mobilenumber', mobilenumber as string)
  }, [])

  useEffect(() => {
    if (watch('province')) {
      const state = provinceSelectOptions?.find((item) => item.value == watch('province'))
      dispatch(setProvince(state?.label as string))
    }
  }, [watch('province')])

  useEffect(() => {
    if (watch('city')) {
      const selectCity = citiesSelectOptions?.find((item) => item.value == watch('city'))
      dispatch(setCity(selectCity?.label as string))
    }
  }, [watch('city')])

  useEffect(() => {
    dispatch(setZone(watch('zone') as string))
  }, [watch('zone')])

  useEffect(() => {
    dispatch(setTransportationType(watch('transportationType') as string))
  }, [watch('transportationType')])

  useEffect(() => {
    dispatch(setPhoneNumber(watch('phoneNumber') as string))
  }, [watch('phoneNumber')])

  useEffect(() => {
    dispatch(setMobileNumber(watch('mobilenumber') as string))
  }, [watch('mobilenumber')])

  const handleRemovePligrimFromReserveList = (nationalCode: string) => {
    const pilgrimList = [...pilgrims]
    const reserveList = pilgrimList?.filter((item) => item.nationalCode !== nationalCode)
    dispatch(setReservePilgrimList(reserveList))
  }

  const handleSubmitForm: SubmitHandler<IForms> = (formData) => {
    if (pilgrims.length >= 6) {
      toastify(ToastType.ERROR, { toastMessage: t('maxPreReserveCapacityIsSixPerson') })
      return
    }

    const hasleader = checkHasLeader(pilgrims)

    if (!hasleader) {
      return toastify(ToastType.ERROR, {
        toastMessage: t('haveToAddLeader'),
      })
    }

    const data: TCreatePreReserve = {
      province: province as string,
      city: city as string,
      zone: Number(formData?.zone) ? Number(formData.zone) : Number(zone),
      phoneNumber: formData.phoneNumber ? formData.phoneNumber : phoneNumber,
      mobilenumber: formData.mobilenumber ? formData.mobilenumber : mobilenumber,
      transportationType: Number(formData.transportationType)
        ? Number(formData.transportationType)
        : Number(transportationType),
      pilgrims: pilgrims as [],
    }
    dispatch(createPreReservePublic({ data }))
  }

  const handleResetForm = () => {
    dispatch(clearStates())
    dispatch(setAuthForm(AuthForm.LOGIN))
  }
  const handleAuthFromAddPilgimClick = () => {
    if (pilgrims.length >= 6) {
      toastify(ToastType.ERROR, { toastMessage: t('maxPreReserveCapacityIsSixPerson') })
      return
    }
    dispatch(setAuthForm(AuthForm.AddPilgrim))
  }

  const getProvinceValue = Number(getValues('province'))

  return (
    <div className='bg-neutral-50 pt-6'>
      <div className='w-4/5 h-screen container mx-auto'>
        <Loader loading={createLoading || provinceLoading || cityLoadin} />

        <FormContainer>
          <FormHeader title='tripPreReserve'>{null}</FormHeader>
          <FormMain>
            <div className='w-full rounded-xl flex flex-col text-sm text-gray-500'>
              {/* Residence Section */}
              <>
                <TitleHeader icon={<FaCity className='w-5 h-5' />} title='residence' />
                <div className='flex flex-col md:flex-row md:items-center justify-start gap-4 text-xs sm:text-sm'>
                  <div className='w-full py-2 rounded-lg mb-4'>
                    <div className='grid grid-cols-1 lg:grid-cols-4 gap-4 -mt-2 items-center'>
                      <Controller
                        defaultValue={provinceSelectOptions?.[getProvinceValue]?.value}
                        name='province'
                        control={control}
                        rules={{
                          required: true,
                        }}
                        render={({ field, formState }) => {
                          return (
                            <SelectField
                              classes='col-start-1 col-strat-2'
                              field={field}
                              formState={formState}
                              lableTitle={t('province')}
                              options={provinceSelectOptions ?? []}
                            />
                          )
                        }}
                      />
                      <Controller
                        name='city'
                        control={control}
                        rules={{
                          required: true,
                        }}
                        render={({ field, formState }) => {
                          return (
                            <SelectField
                              field={field}
                              formState={formState}
                              lableTitle={t('city')}
                              options={citiesSelectOptions}
                            />
                          )
                        }}
                      />
                      <InputField
                        id={'zone'}
                        lableTitle={t('zone')}
                        errors={errors as FieldErrorsImpl<FieldValues>}
                        {...register('zone')}
                      />
                    </div>
                  </div>
                </div>
              </>

              {/* Trip Infomation Section */}
              <div className='md:mt-2'>
                <TitleHeader
                  classes='bg-white'
                  icon={<MdOutlineTravelExplore className='w-5 h-5' />}
                  title='tripInfomation'
                />
                <form className='w-full h-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-center justify-between'>
                  <Controller
                    name='transportationType'
                    control={control}
                    rules={{
                      required: true,
                    }}
                    render={({ field, formState }) => {
                      return (
                        <SelectField
                          classes='col-start-1 col-strat-2'
                          field={field}
                          formState={formState}
                          lableTitle={t('transportation')}
                          options={transportationSelectOptions}
                        />
                      )
                    }}
                  />
                  <InputField
                    id={'mobilenumber'}
                    lableTitle={t('mobile')}
                    errors={errors as FieldErrorsImpl<FieldValues>}
                    maxLength={11}
                    placeholder='09121231234'
                    {...register('mobilenumber')}
                  />
                  <InputField
                    id={'phoneNumber'}
                    lableTitle={t('telephone')}
                    errors={errors as FieldErrorsImpl<FieldValues>}
                    maxLength={11}
                    placeholder='22334455 021'
                    {...register('phoneNumber')}
                  />
                </form>
              </div>

              {/* Travelers */}
              <>
                <div className='mt-7 md:mt-9'>
                  <TitleHeader icon={<HiOutlineUsers className='w-5 h-5' />} title='travelers' />
                </div>
                <div className='h-12 flex items-center justify-end bg-transparent'>
                  <Button
                    types='success'
                    icon={<PlusIcon />}
                    onClick={handleAuthFromAddPilgimClick}
                  >
                    {t('addNewPilgrim')}
                  </Button>
                </div>
                <div className='overflow-auto scrollbar'>
                  <div className='flex flex-col items-center justify-start mt-2 min-w-max'>
                    <HeaderInfo
                      data={NewReservePilgrimHeaderData}
                      classes='-mt-1 grid grid-cols-7'
                    />
                    <PilgrimCard
                      handleClick={handleRemovePligrimFromReserveList}
                      canRemoveLeader={true}
                      updateBirthday={false}
                      data={pilgrims}
                      cols={7}
                      classes='mt-2'
                      cardType='Remove'
                    />
                  </div>
                </div>
              </>
              <div className='md:mt-10 mt-6 flex items-center justify-end'>
                <CancleButton path='/' onClick={handleResetForm}>
                  {t('cancle')}
                </CancleButton>
                <Button
                  disabled={createLoading || pilgrims.length === 0}
                  loading={createLoading}
                  types='success'
                  classes='px-12 mr-2'
                  onClick={handleSubmit(handleSubmitForm)}
                >
                  {t('save')}
                </Button>
              </div>
            </div>
          </FormMain>
        </FormContainer>
      </div>
    </div>
  )
}

export default PublicPilgrimsPreReserve
